import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../services/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import useAdminStore from '../../store/adminStore';
import PlusDurationFields, { inferPlusDurationFromUser } from './PlusDurationFields';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const getRoleOptions = (t) => [
  { value: 'admin', label: t('userRoleModal.roles.admin') },
  { value: 'plus', label: t('userRoleModal.roles.plus') },
  { value: 'free', label: t('userRoleModal.roles.free') },
];

const UserRoleModal = ({ user, isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation('admin');
  const ROLE_OPTIONS = getRoleOptions(t);
  const { authToken, user: currentUser } = useAuth();
  const { updateUserInList } = useAdminStore();
  const [role, setRole] = useState('free');
  const [plusDurationType, setPlusDurationType] = useState('days');
  const [plusDurationDays, setPlusDurationDays] = useState(30);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setRole(user.role || 'free');
      const inferred = inferPlusDurationFromUser(user);
      setPlusDurationType(inferred.plusDurationType);
      setPlusDurationDays(inferred.plusDurationDays);
    }
  }, [user]);

  const showToast = (title, type = 'success') => {
    window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
      detail: { type, title },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    try {
      const payload = {
        token: authToken,
        userId: user.id,
        role,
      };

      if (role === 'plus') {
        payload.plusDurationType = plusDurationType;
        payload.plusDurationDays = plusDurationType === 'days' ? plusDurationDays : undefined;
      }

      const result = await adminApi.updateRole(payload);

      if (result.success) {
        updateUserInList(user.id, {
          role,
          plusExpiryDate: result.user?.plusExpiryDate || null,
          plusSource: result.user?.plusSource || null,
        });
        if (String(currentUser?.id) === String(user.id)) {
          window.dispatchEvent(new CustomEvent('notesplus-role-updated', {
            detail: { role },
          }));
          window.dispatchEvent(new CustomEvent('notesplus-refresh-user'));
        }
        showToast(t('userRoleModal.toasts.roleUpdated'));
        onSuccess?.();
        onClose();
      } else {
        const errorMap = {
          PLUS_DURATION_REQUIRED: t('userRoleModal.toasts.plusDurationRequired'),
          INVALID_PLUS_DURATION: t('userRoleModal.toasts.invalidPlusDuration'),
        };
        showToast(errorMap[result.error] || result.error || t('userRoleModal.toasts.updateFailedFallback'), 'error');
      }
    } catch (error) {
      showToast(getApiErrorMessage(error, { fallback: t('userRoleModal.toasts.updateErrorFallback') }), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && user && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000]"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-[100001] flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#0d1424] rounded-2xl shadow-[0_32px_80px_rgba(0,0,0,0.7)] w-full max-w-md border border-white/[0.08]">
              <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#3d8ef0]/10 border border-[#3d8ef0]/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#3d8ef0]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">{t('userRoleModal.title')}</h2>
                    <p className="text-xs text-[#7a96b0] mt-0.5">{user.name || user.email}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-white/[0.05] text-[#7a96b0] hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#8aaccc] mb-2">
                    {t('userRoleModal.userRoleLabel')}
                  </label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full px-4 py-2.5 bg-[#161f31] border border-white/[0.08] rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/40"
                  >
                    {ROLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {role === 'plus' && (
                  <PlusDurationFields
                    plusDurationType={plusDurationType}
                    setPlusDurationType={setPlusDurationType}
                    plusDurationDays={plusDurationDays}
                    setPlusDurationDays={setPlusDurationDays}
                  />
                )}

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-[#7a96b0] hover:text-white hover:bg-white/[0.05] transition-colors"
                  >
                    {t('userRoleModal.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#3d8ef0] hover:bg-[#5ca3f5] text-white transition-colors disabled:opacity-60"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    <span>{t('userRoleModal.save')}</span>
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default UserRoleModal;
